/**
 * QueueJoin — the customer-facing mobile surface (route `queue.join`, QR /
 * join link, no admin shell). It WALKS THE DESIGNED JOURNEY PAGES (PM
 * direction 2026-07-17): Services → Wait Time → Contact Details →
 * Waiting in queue → How did we do? — each rendered from the queue journey's
 * page configs (getPublicQueue returns them resolved). Params: q=<queueId>,
 * e=<entryId>; the entry id persists in the URL so a reload keeps the place.
 */
import { useMemo, useState } from "react";
import { alpha, Banner, Btn, Card, ChatBubble, Chip, ChipToggleGroup, Field, Icon, IconTile, InlineEmptyState, SectionLabel, SegmentedControl, SelectableTile, Spinner, SummaryList, SummaryRow, T } from "@v0y4ge/jrni-ui";
import { useNav } from "../../shell/router";
import { can } from "../../shell/access";
import { NumberField } from "../../lib/NumberField";
import { useWfResource } from "../workforce/useWorkforce";
import { qpDefaultCfg, qpTheme, qpBlocks, qpFontFamily, qpOnAccent, qDesignTheme, QContentBlock, type QPageId, type QTheme } from "./qp-pages";
import { LandingPreview, type LandingBlock, type LandingTheme } from "../landing/landing-builder-helpers";
import { isQueueBlock, QueueBlockView, qResolveQuestionSections, qQuestionLibFrom, qServiceQuestionRefs, QQuestionField, type QRuntime, type QQuestionLib, type QQuestionRef, type QQuestionSection, type QQField } from "./qblocks";
import { useQuestions } from "../questions/useQuestions";
import { useServices } from "../services/services-data";
import { useSurveys } from "../surveys/SurveyEditorPage";
import { useSurveyQuestions } from "../surveys/survey-questions-store";
import { getPublicQueue, joinPublicQueue, leavePublicQueue, sendPublicQueueMessage, submitPublicQueueSurvey, type PublicQueueView, type MsgChannel } from "./queues-api";

import { useT } from "@v0y4ge/i18n-runtime";
// Per-channel external brand colors (generic SMS green / WhatsApp brand green) —
// no jrni token represents a specific third-party brand mark, so these hexes are
// vendor DATA. The marker has to sit ON the declaration line (a marker ABOVE the
// `const` is cut off by the audit's column-0 scan, which is why these two still
// read as unmarked before this pass).
const CH_META: Record<MsgChannel, { label: string; color: string }> = { // ui-config: third-party brand marks
  sms: { label: "Text (SMS)", color: "#1D7A3A" },
  whatsapp: { label: "WhatsApp", color: "#25D366" },
};
type Mode = "required" | "optional" | "off";
// ui-raw (this recipe and every control that spreads it): the CONTROL BOX only.
// The label / required-marker / error half of every field on this screen IS the
// jrni-ui `Field` component. What cannot come from `Input`/`Textarea` is the box
// itself: `Input` fixes its height at `size === "sm" ? 32 : 40` and its field
// text at a hardcoded `fontSize: 14`, and `Textarea` fixes its text at
// `T.text.body.fontSize` — none of the three is exposed as a prop. Adopting
// them here would drop these touch targets from 48px below the 44px minimum AND
// put the field text under 16px, which makes mobile Safari zoom the whole page
// on focus. Filed as a proposal (Input/Textarea touch-size tier); the fontSize
// below is the 16px no-zoom threshold and is deliberately off the type ramp.
// TOKEN ROLE — `background` and `color` were ABSENT from this recipe, so every
// field fell back to the user-agent `field`/`fieldtext` pair: under
// [data-theme="dark"] the inputs rendered as solid WHITE slabs inside the dark
// card, and the survey textarea (which the reset gives `color: inherit`) drew
// near-white text on that white fill — illegible. Both tokens flip with the
// theme, and they are exactly what jrni-ui `Input`/`Textarea` set
// (`bg = inert ? T.n6 : T.white`, `color: T.n1`), so the raw box now matches the
// DS control it is deliberately standing in for. Reproduced + fixed 2026-08-11.
const input = { width: "100%", minHeight: 48, padding: `0 ${T.space[4]}`, border: `1px solid ${T.n4}`, borderRadius: T.radius.default, fontSize: 16, fontFamily: T.font, background: T.white, color: T.n1, boxSizing: "border-box" as const };
// The primary action. Every one of the 7 call sites is a `<Btn>`, so this recipe
// now overrides ONLY geometry + type and lets Btn paint its own `primary`
// treatment (brandFill + onColor + its CSS hover/active/focus state layer).
//
// TOKEN ROLE, the long way round: it used to be `background: T.n1` + `color:
// T.white`. Both tokens invert, so contrast survived but the DESIGN inverted —
// the inked near-black CTA rendered as a WHITE pill with dark ink under
// [data-theme="dark"] (visible in this migration's dark renders; it is
// ds-semantic-audit's DS-PAIR-P2 defect). `T.chromeInk` was tried next and is
// worse HERE: fixed #1F2430 sits on a dark card surface of #212633, so the button (ui-raw: hex in prose)
// all but vanished (verified by render, not by reading tokens). Dropping the pair
// entirely is the right answer — a primary action should be the DS's primary
// button, and this page's own tenant accent (`qth.accent`) is applied to the
// brand tile, never to the CTA, so nothing tenant-specific is lost.
const cta = { minHeight: 52, borderRadius: T.radius.lg, fontSize: T.text.h3.fontSize, fontWeight: 800 } as const;
const linkBtn = { minHeight: 50, borderRadius: T.radius.lg, border: `1.5px solid ${T.border}`, background: T.white, color: T.n1, fontSize: T.text.h3.fontSize, fontWeight: 700, fontFamily: T.font, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: T.space[2], textDecoration: "none" } as const;

export default function QueueJoin() {
  const t = useT();
  const nav = useNav();
  const queueId = (nav.params?.q ?? "") as string;
  const entryId = (nav.params?.e ?? null) as string | null;
  const { data, loading, error, reload } = useWfResource<PublicQueueView | null>(
    `qpublic|${queueId}|${entryId ?? "-"}`,
    () => (queueId ? getPublicQueue(queueId, entryId) : Promise.resolve(null)),
    { pollMs: 5_000 });

  // Pre-join walk state (// ui-local — step navigation before the join call)
  const [walk, setWalk] = useState(0);
  const [service, setService] = useState<string>("");
  const [locId, setLocId] = useState<string>(""); // Location-picker page choice (// ui-local until join)
  const [locSort, setLocSort] = useState<"distance" | "wait">("distance"); // ui-local — customer sort toggle
  const [first, setFirst] = useState(""); const [last, setLast] = useState("");
  const [phone, setPhone] = useState(""); const [email, setEmail] = useState("");
  const [channel, setChannel] = useState<MsgChannel>("sms");
  const [party, setParty] = useState(1);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Waiting-page messaging (// ui-local composer state; send is awaited)
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  // CSAT / left-without-service feedback (// ui-local answer state; submit is awaited)
  const [svAnswers, setSvAnswers] = useState<Record<string, string | number>>({});
  const [svDone, setSvDone] = useState(false);
  const [svBusy, setSvBusy] = useState(false);
  // ui-local — the feedback survey reveals when the customer taps the button.
  const [fbOpen, setFbOpen] = useState(false);
  // ui-local — admin flow-testing simulation ("Simulate It's Your Turn"): only
  // rendered for signed-in staff sessions (the customer QR surface has no
  // session, so can() is false). Never touches the real entry.
  const [simStage, setSimStage] = useState<"none" | "called" | "done">("none");

  const pages = (data?.queue.pages ?? []) as { page: QPageId; cfg: Record<string, any> }[];
  // Booking-question refs on the designed qcontact block resolve against the
  // questions library. INTERIM: the backend should return `resolvedSections`
  // on the contact page cfg (declared 2026-07-20 changelog); until then the
  // client resolves refs read-only via the questions store.
  const qlibStore = useQuestions();
  const questionLib: QQuestionLib = useMemo(() => qQuestionLibFrom(qlibStore),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qlibStore.loading, qlibStore.questions().length, qlibStore.groups().length]);
  // Service-tied booking questions (refs live on the service's delivery-method
  // config) — resolved for the picked service so question content varies with
  // the service selection, exactly as on the staff walk-up surface.
  const svcStore = useServices();
  const svcSectionsFor = (svc: string): QQuestionSection[] =>
    qResolveQuestionSections(qServiceQuestionRefs(svc, svcStore.services()), questionLib);
  const sectionsFor = (blockCfg: Record<string, any>): QQuestionSection[] =>
    (blockCfg.resolvedSections as QQuestionSection[] | undefined)
    ?? qResolveQuestionSections(blockCfg.questions as QQuestionRef[] | undefined, questionLib);
  // Surveys resolve client-side from the page's picked surveyId (INTERIM — same
  // seam as booking questions above; backend should return the resolved survey).
  const { surveys: svLib } = useSurveys();
  const sqStore = useSurveyQuestions(); // survey items reference the SURVEY-question library, not booking questions
  const svResolve = (id?: string | null): NonNullable<PublicQueueView["survey"]> | null => {
    if (!id) return null;
    const s = svLib.find((x) => x.id === id);
    if (!s) return null;
    const qs = sqStore.questions();
    return {
      id: s.id, name: s.name, submitted: false,
      questions: (s.items || []).map((it: { qid: string }) => { const q = qs.find((x) => x.id === it.qid); return q ? { id: q.id, label: q.label, type: (/rating|scale|nps/i.test(String((q as { type?: string }).type || "")) ? "rating" : "text") as "rating" | "text" } : null; }).filter(Boolean) as { id: string; label: string; type: "rating" | "text" }[],
    };
  };
  const cfgOf = (p: QPageId): Record<string, any> => pages.find((x) => x.page === p)?.cfg ?? qpDefaultCfg(p);
  /** The page's landing-engine design (built in the Queue Page Builder) — when
   *  present the customer sees EXACTLY that page, blocks + theme + font. */
  const designOf = (p: QPageId) => {
    const d = cfgOf(p).design as { blocks?: LandingBlock[]; theme?: string; font?: string; brandTheme?: LandingTheme } | undefined;
    return d && Array.isArray(d.blocks) && d.blocks.length ? d : null;
  };
  const hasPage = (p: QPageId) => pages.length === 0 || pages.some((x) => x.page === p);
  // The pre-join walk: only the pages the journey actually includes. The
  // Pre-booking and Location-picker pages are OPTIONAL — they must be
  // explicitly in the journey (legacy/default journeys never get them
  // implicitly). Pre-booking always goes FIRST.
  const walkPages: QPageId[] = (["prebook", "services", "locations", "wait", "contact"] as QPageId[]).filter((p) =>
    p === "locations" || p === "prebook" ? pages.some((x) => x.page === p) : hasPage(p));
  const curPage = walkPages[Math.min(walk, walkPages.length - 1)] ?? "contact";

  // Page branding + content blocks designed in the Queue Page Builder.
  const themeOf = (p: QPageId): QTheme => qpTheme(cfgOf(p));
  const contentOf = (p: QPageId): { before: React.ReactNode; after: React.ReactNode } => {
    const th = themeOf(p);
    const bl = qpBlocks(cfgOf(p));
    const ci = bl.findIndex((b) => b.type === "core");
    const render = (list: typeof bl) => list.filter((b) => b.type !== "brand" && b.type !== "core").map((b) => <QContentBlock key={b.uid} block={b} theme={th} />);
    return { before: render(bl.slice(0, ci < 0 ? 0 : ci)), after: render(bl.slice(ci < 0 ? bl.length : ci + 1)) };
  };

  /** Pre-booking answers travel with the join: the prebook page's question refs
   *  (designed block or page cfg), resolved + flattened. */
  const prebookQs = (): { id: string; label: string }[] => {
    if (!pages.some((x) => x.page === "prebook")) return [];
    const d = designOf("prebook");
    const refs = (d ? (d.blocks as LandingBlock[]).find((b) => b.type === "qprebook")?.cfg?.questions : cfgOf("prebook").bookingQuestions) as QQuestionRef[] | undefined;
    return qResolveQuestionSections(refs, questionLib).flatMap((s) => s.questions);
  };

  const join = async (bcfg?: Record<string, any>) => {
    // Field modes + labels come from the designed qcontact BLOCK when the page
    // was built in the page builder (bcfg), else from the legacy page cfg.
    const pc = cfgOf("contact");
    const c = { ...pc, ...(bcfg ?? {}) };
    const f = (bcfg
      ? { firstName: bcfg.firstName ?? "required", lastName: bcfg.lastName ?? "required", mobile: bcfg.mobile ?? "required", email: bcfg.email ?? "optional" }
      : (c.fields ?? {})) as Record<string, Mode>;
    const need = (m: Mode | undefined, v: string) => m === "required" && !v.trim();
    if (need(f.firstName, first) || need(f.lastName, last) || need(f.mobile, phone) || need(f.email, email)) { setErr("Please fill in the required fields."); return; }
    setBusy(true); setErr(null);
    try {
      // Answered questions: pre-booking questions + the designed qcontact
      // block's resolved sections (groups flattened) + legacy resolvedQuestions
      // + the picked service's own booking questions — deduped by id.
      const sectQs = bcfg ? sectionsFor(bcfg).flatMap((s) => s.questions) : [];
      const svcQs = svcSectionsFor(service || data?.queue.services[0] || "").flatMap((s) => s.questions);
      const allQs = [...prebookQs(), ...sectQs, ...((c.resolvedQuestions ?? []) as { id: string; label: string }[]), ...svcQs];
      const rq = allQs.filter((x, i) => allQs.findIndex((y) => y.id === x.id) === i);
      const r = await joinPublicQueue(queueId, {
        name: `${first.trim()} ${last.trim()}`.trim() || first.trim(), phone: phone.trim(), email: email.trim() || null,
        channel: c.askChannel === false ? "sms" : channel, party: c.askParty === false ? 1 : party,
        service: service || data?.queue.services[0] || null,
        locationId: locId || null,
        answers: rq.map((q) => ({ q: q.label, a: answers[q.id] ?? "" })).filter((a) => a.a),
      });
      nav.replace("queue.join", { q: queueId, e: r.entryId });
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };
  const leave = async () => {
    if (!entryId) return;
    setBusy(true);
    try { await leavePublicQueue(queueId, entryId); reload(); } // entry stays in the URL — the Left-without-service page shows next
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };
  const sendMsg = async (text: string) => {
    if (!entryId || !text.trim()) return;
    setMsgSending(true); setErr(null);
    try { await sendPublicQueueMessage(queueId, entryId, text.trim()); setMsgText(""); reload(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setMsgSending(false);
  };
  const submitSurveyFor = async (sv: { id: string; name: string; questions: { id: string; label: string; type: "rating" | "text" }[]; submitted?: boolean } | null | undefined) => {
    if (!sv || !entryId) return;
    setSvBusy(true); setErr(null);
    try {
      await submitPublicQueueSurvey(queueId, entryId, sv.id, sv.questions.map((q) => ({ questionId: q.id, value: svAnswers[q.id] ?? "" })).filter((a) => a.value !== ""));
      setSvDone(true);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setSvBusy(false);
  };
  const submitSurvey = async () => { await submitSurveyFor(data?.survey ?? null); };

  const card = (children: React.ReactNode, foot?: React.ReactNode, th?: QTheme, pageForContent?: QPageId) => {
    const qth = th ?? themeOf(curPage);
    const cb = pageForContent ? contentOf(pageForContent) : null;
    const name = qth.brandName || data?.queue.org || "Queue";
    // Queue hero — default page-top display: the queue's hero image, else the brand gradient.
    // ui-raw: fixed two-tone brand gradient fallback (#75ADFA is already
    // allow-listed as decorative) — kept both stops literal so the pair
    // renders identically regardless of tenant/dark theme, rather than
    // reacting on only one stop.
    const heroBg = data?.queue?.heroImage ? `url(${data.queue.heroImage}) center/cover` : "linear-gradient(135deg, #75ADFA, #5142C7)";
    // TOKEN ROLE — the page background is TENANT DATA (`qth.bg`), a fixed hex that
    // does NOT flip with the theme. The header/footer ink on top of it was `T.n1`
    // and `T.soft`, which DO flip: under [data-theme="dark"] `T.n1` resolves to
    // #ECEEF3 and the org name went near-invisible on a light tenant background (ui-raw: hex in prose)
    // (reproduced in the dark render of this migration). Derive the ink FROM the
    // fill instead, with the same contrast helper the brand tile already uses.
    const onBg = qpOnAccent(qth.bg);
    return (
    <div style={{ minHeight: "100vh", background: qth.bg, display: "flex", flexDirection: "column", alignItems: "center", padding: `${T.space[6]} ${T.space[4]}`, fontFamily: qpFontFamily(qth) }}>
      <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", gap: T.space[4] }}>
        <div style={{ display: "flex", alignItems: "center", gap: T.space[2], padding: `0 ${T.space[1]}` }}>
          {/* Not `IconTile`: the ink on this tile is computed FROM the tenant's own
              accent (`qpOnAccent`) so a pale brand colour still reads. IconTile's
              solid variant forces `T.onColor` for an explicit `color`, which would
              put white initials on a pale tenant accent. */}
          {qth.logoUrl
            ? <img src={qth.logoUrl} alt="" style={{ width: 36, height: 36, borderRadius: T.radius.md, objectFit: "cover", flexShrink: 0 }} />
            : <span style={{ width: 36, height: 36, borderRadius: T.radius.md, background: qth.accent, color: qpOnAccent(qth.accent), display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: T.text.h3.fontSize }}>{name.slice(0, 1)}</span>}
          <div>
            <div style={{ fontSize: T.text.h3.fontSize, fontWeight: 700, color: onBg }}>{name}</div>
            <div style={{ fontSize: T.text.label.fontSize, color: alpha(onBg, 70) }}>{data?.queue.name ?? ""}</div>
          </div>
        </div>
        {/* The journey surface is a DS `Card`: borderless + elevated, radius `lg`
            (the scale's large-panel step) and `overflow: hidden` so the hero band
            can bleed to its corners. `padding="none"` because the body below owns
            its own padding under the full-bleed band. */}
        <Card padding="none" bordered={false} style={{ borderRadius: T.radius.lg, boxShadow: T.shadowLg, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div aria-hidden style={{ height: 110, background: heroBg, flexShrink: 0 }} data-testid="qjoin-hero-band" />
          <div style={{ padding: T.space[6], display: "flex", flexDirection: "column", gap: T.space[4] }}>
            {cb?.before}
            {children}
            {cb?.after}
          </div>
        </Card>
        {foot ?? <div style={{ fontSize: T.text.label.fontSize, color: alpha(onBg, 66), textAlign: "center" }}>{t('queues.join.text')}{" "}{CH_META[data?.entry?.channel ?? channel].label}{t('queues.join.text2')}</div>}
      </div>
    </div>
    );
  };

  // The three load states get DESIGNED treatments, and the two NOTICE states use
  // the same one (`Banner`) rather than a bare tinted <div> each: a bad link is a
  // warning, a failed fetch is a danger (Banner renders it role="alert").
  if (!queueId) return card(<Banner tone="warning" dismissible={false} testid="qjoin-no-queue">{t('queues.join.text3')}</Banner>);
  if (loading && !data) return card(
    <div role="status" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: T.space[2], padding: `${T.space[4]} 0`, fontSize: T.text.body.fontSize, color: T.soft }} data-testid="qjoin-loading">
      <Spinner size="md" label={t('queues.join.text4')} />{t('queues.join.text4')}
    </div>);
  if (error || !data) return card(
    <Banner tone="danger" dismissible={false} title={t('queues.join.text5')} testid="qjoin-load-error">
      {error || "This link may be out of date — please scan the QR code at the location again."}
    </Banner>);

  const q = data.queue;
  const realMe = data.entry;
  const canSim = !!realMe && realMe.status !== "left" && can("queues.read");
  const simActive = canSim && simStage !== "none";
  const me = simActive && realMe
    ? { ...realMe, status: (simStage === "called" ? "called" : "done") as NonNullable<typeof realMe>["status"], position: simStage === "called" ? 1 : realMe.position, messages: simStage === "called" ? [...(realMe.messages ?? []), { from: "team" as const, text: "It's your turn — please come up now." }] : realMe.messages }
    : realMe;
  // The admin-only flow-testing pills are the DS `Chip` (pill shell + selected
  // state + focus ring), not three hand-rolled <button>s. Only the drop shadow
  // that lifts them off the hero band stays local.
  const simChip: React.CSSProperties = { boxShadow: T.shadowSm };
  const simBar = canSim ? (
    <div style={{ display: "flex", justifyContent: "flex-end", gap: T.space[2], flexWrap: "wrap" }}>
      {simStage === "none"
        ? <Chip onClick={() => setSimStage("called")} style={simChip} testid="qjoin-simulate-called-btn">{t('queues.join.text6')}</Chip>
        : <>
            <Chip onClick={() => setSimStage("none")} selected style={simChip} testid="qjoin-simulate-reset-btn">{t('queues.join.text7')}</Chip>
            {simStage === "called" ? <Chip onClick={() => setSimStage("done")} style={simChip} testid="qjoin-simulate-done-btn">{t('queues.join.text8')}</Chip> : null}
          </>}
    </div>
  ) : null;

  /* ── Live runtime + renderer for DESIGNED pages (cfg.design) ──────── */
  const estNow = (q.serviceWaits ?? {})[service] ?? q.estWaitMins;
  const rt: QRuntime = {
    live: true,
    services: q.services, serviceWaits: q.serviceWaits ?? {}, estWaitMins: me?.estWaitMins ?? estNow, waiting: q.waiting,
    serviceGroups: q.serviceGroups,
    service: service || q.services[0] || "",
    onPickService: (s) => { setService(s); setWalk((x) => x + 1); },
    locationOptions: (q.nearbyLocations ?? []).map((l) => ({ id: l.id, name: l.name, distanceKm: l.distanceKm, waitMins: (l.serviceWaits ?? {})[service || q.services[0] || ""] ?? l.estWaitMins ?? q.estWaitMins })),
    pickedLocationId: locId,
    onPickLocation: (id) => { setLocId(id); setWalk((x) => x + 1); },
    onContinue: () => setWalk((x) => x + 1),
    first, last, phone, email, channel, party,
    setField: (k, v) => { ({ first: setFirst, last: setLast, phone: setPhone, email: setEmail, channel: setChannel, party: setParty } as Record<string, (x: never) => void>)[k](v as never); },
    questions: (cfgOf("contact").resolvedQuestions ?? []) as { id: string; label: string; options?: string[] }[],
    answers, setAnswer: (id, v) => setAnswers((a) => ({ ...a, [id]: v })),
    busy, err, onJoin: (bc) => { void join(bc as Record<string, any>); },
    position: me?.position ?? 0,
    entryStatus: me?.status === "done" ? "done" : me?.status === "serving" ? "serving" : me?.status === "called" ? "called" : "waiting",
    entryName: me?.name ?? "",
    messages: (me?.messages ?? []) as { from: string; text: string }[],
    msgText, setMsgText, msgSending, onSendMsg: (t) => { void sendMsg(t); },
    onLeave: () => { void leave(); },
    survey: data.survey ?? null,
    svAnswers, setSvAnswer: (id, v) => setSvAnswers((a) => ({ ...a, [id]: v })),
    svDone, svBusy, onSubmitSurvey: () => { void submitSurvey(); },
  };
  const designedPage = (p: QPageId, chromeTop?: React.ReactNode) => {
    const d = designOf(p)!;
    const th = qDesignTheme(d);
    return (
      <div style={{ minHeight: "100vh", background: T.n6 }}>
        <div style={{ width: "100%", maxWidth: 720, margin: "0 auto", padding: `${T.space[4]} ${T.space[3]} ${T.space[8]}`, boxSizing: "border-box", display: "flex", flexDirection: "column", gap: T.space[3] }}>
          {chromeTop}
          {/* Same DS `Card` surface as the non-designed pages, so the two page
              renderers can't drift apart. `padding="none"`: LandingPreview owns
              its own internal padding. */}
          <Card padding="none" bordered={false} style={{ borderRadius: T.radius.lg, overflow: "hidden", boxShadow: T.shadowLg }}>
            <LandingPreview blocks={d.blocks as LandingBlock[]} theme={th} font={d.font} event={null} chrome={false} mode="live"
              renderCustom={(b) => {
                if (!isQueueBlock(b.type)) return null;
                let r = rt;
                if (b.type === "qcontact") r = { ...rt, questionSections: [...sectionsFor(b.cfg as Record<string, any>), ...svcSectionsFor(service || q.services[0] || "")] };
                if (b.type === "qprebook") r = { ...rt, questionSections: sectionsFor(b.cfg as Record<string, any>) };
                if (b.type === "qsurvey" || b.type === "qnoservice") { const sv = svResolve((b.cfg as Record<string, any>).surveyId) ?? rt.survey; r = { ...rt, survey: sv, onSubmitSurvey: () => { void submitSurveyFor(sv); } }; }
                return <QueueBlockView block={b} theme={th} rt={r} />;
              }} />
          </Card>
        </div>
      </div>
    );
  };

  /** Plain (non-designed) survey questions + submit — shared by the How-did-we-do
   *  and Left-without-service pages. */
  const surveyQuestionsUI = (sv: NonNullable<PublicQueueView["survey"]>, submitLabel: string) => (
    <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.space[4], display: "flex", flexDirection: "column", gap: T.space[4] }}>
      <div style={{ fontSize: T.text.body.fontSize, fontWeight: 700, color: T.n1 }}>{sv.name}</div>
      {sv.questions.map((qq) => (
        // label + control + (future) hint is the DS `Field`; it also owns the
        // required marker, replacing this screen's local `label`/`req` copies.
        <Field key={qq.id} label={qq.label}>
          {qq.type === "rating" ? (
            // The 1-5 CSAT scale IS single-select over a small ordered set, which
            // is `SegmentedControl` (full-width = equal segments). The previous
            // hand-rolled row also mis-announced itself: five `aria-pressed`
            // buttons instead of one radiogroup, so a screen reader never heard
            // "1 of 5". Value is numeric on the wire, string in the control.
            <SegmentedControl
              full size="lg" ariaLabel={qq.label}
              options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))}
              value={svAnswers[qq.id] != null ? String(svAnswers[qq.id]) : ""}
              onChange={(v) => setSvAnswers((a) => ({ ...a, [qq.id]: Number(v) }))}
              testid={`qjoin-survey-rating-${qq.id}`}
            />
          ) : (
            // ui-raw: same control-box reason as the `input` recipe above —
            // jrni-ui `Textarea` fixes its field text at `T.text.body.fontSize`
            // (14px) with no prop to raise it, which is under the 16px mobile-
            // Safari no-zoom threshold every other field on this page holds.
            <textarea value={String(svAnswers[qq.id] ?? "")} onChange={(e) => setSvAnswers((a) => ({ ...a, [qq.id]: e.target.value }))} rows={3}
              data-testid={`qjoin-survey-text-${qq.id}`}
              style={{ ...input, minHeight: 0, padding: T.space[3], resize: "vertical" }} />
          )}
        </Field>
      ))}
      <Btn onClick={() => { void submitSurveyFor(sv); }} disabled={svBusy} testid="qjoin-survey-submit" full
        style={{ ...cta, height: 52, padding: 0, opacity: svBusy ? .6 : 1 }}>{svBusy ? t('queues.join.text9') : submitLabel}</Btn>
    </div>
  );

  /* ── Left without service — REQUIRED page for unserved exits ──────── */
  if (me && me.status === "left") {
    if (designOf("noservice")) return designedPage("noservice");
    const c = cfgOf("noservice");
    const sv = svResolve(c.surveyId) ?? data.survey ?? null;
    return card(
      <>
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: T.space[2], padding: `${T.space[2]} 0` }}>
          {/* Hero glyph = `IconTile` circle. Its own tone map already resolves
              danger to exactly the red2 fill / red1 glyph this hand-rolled span
              wrote out, so the tone pair can no longer drift from Banner's. */}
          <IconTile shape="circle" tone="danger" size="lg" icon={<Icon.warn size={26} />} style={{ width: 54, height: 54, margin: "0 auto" }} />
          <div style={{ fontSize: T.text.display.fontSize, fontWeight: 800, color: T.n1 }}>{c.headline}</div>
          <div style={{ fontSize: T.text.body.fontSize, color: T.soft }}>{c.sub}</div>
        </div>
        {sv && !svDone && !sv.submitted ? (
          !fbOpen
            ? <Btn onClick={() => setFbOpen(true)} testid="qjoin-noservice-open-survey" full style={{ ...cta, height: 52, padding: 0 }}>{c.buttonLabel ?? "Share what happened"}</Btn>
            : surveyQuestionsUI(sv, "Send feedback")
        ) : sv || svDone ? (
          <Banner tone="success" size="compact" dismissible={false} testid="qjoin-noservice-thanks">{t('queues.join.text10')}</Banner>
        ) : null}
        {(c.showBook !== false || c.showBrowse !== false) ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], borderTop: `1px solid ${T.border}`, paddingTop: T.space[4] }}>
            {c.showBook !== false ? <a href={c.bookUrl || undefined} target="_blank" rel="noopener noreferrer" style={linkBtn} data-testid="qjoin-noservice-book-link"><Icon.calendar size={16}/>{c.bookLabel ?? "Book an appointment instead"}</a> : null}
            {c.showBrowse !== false ? <a href={c.browseUrl || undefined} target="_blank" rel="noopener noreferrer" style={linkBtn} data-testid="qjoin-noservice-browse-link"><Icon.globe size={16}/>{c.browseLabel ?? "Browse our Services and Experiences"}</a> : null}
          </div>
        ) : null}
        {err ? <Banner tone="danger" size="compact" dismissible={false} testid="qjoin-noservice-error">{err}</Banner> : null}
      </>,
      undefined, themeOf("noservice"), "noservice"
    );
  }

  /* ── In the queue / done — the Waiting + How-did-we-do pages ────────── */
  if (me && me.status !== "left") {
    const done = me.status === "done";
    if (done) {
      if (designOf("csat")) return designedPage("csat", simBar);
      const c = cfgOf("csat"); const sv = svResolve(c.surveyId) ?? data!.survey;
      return card(
        <>
          {simBar}
          <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: T.space[2], padding: `${T.space[2]} 0` }}>
            {/* Sibling of the Left-without-service hero above — same component,
                success tone (its map is the same green2/green1 pair). */}
            <IconTile shape="circle" tone="success" size="lg" icon={<Icon.check size={26} />} style={{ width: 54, height: 54, margin: "0 auto" }} />
            <div style={{ fontSize: T.text.display.fontSize, fontWeight: 800, color: T.n1 }}>{c.headline}</div>
            <div style={{ fontSize: T.text.body.fontSize, color: T.soft }}>{c.sub}</div>
          </div>
          {sv && !svDone && !sv.submitted ? (
            !fbOpen
              ? <Btn onClick={() => setFbOpen(true)} testid="qjoin-csat-open-survey" full style={{ ...cta, height: 52, padding: 0 }}>{c.buttonLabel ?? "Give feedback"}</Btn>
              : surveyQuestionsUI(sv, "Send feedback")
          ) : sv || svDone ? (
            <Banner tone="success" size="compact" dismissible={false} testid="qjoin-csat-thanks">{t('queues.join.text10')}</Banner>
          ) : null}
          {(c.showBrowse !== false || c.showReview !== false) ? (
            <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], borderTop: `1px solid ${T.border}`, paddingTop: T.space[4] }}>
              {c.showBrowse !== false ? <a href={c.browseUrl || undefined} target="_blank" rel="noopener noreferrer" style={linkBtn} data-testid="qjoin-csat-browse-link"><Icon.globe size={16}/>{c.browseLabel ?? "Browse our Services and Experiences"}</a> : null}
              {c.showReview !== false ? <a href={c.reviewUrl || undefined} target="_blank" rel="noopener noreferrer" style={linkBtn} data-testid="qjoin-csat-review-link"><Icon.star size={16}/>{c.reviewLabel ?? "Leave a Google Review"}</a> : null}
            </div>
          ) : null}
          {err ? <Banner tone="danger" size="compact" dismissible={false} testid="qjoin-csat-error">{err}</Banner> : null}
        </>,
        undefined, themeOf("csat"), "csat"
      );
    }
    if (designOf("waiting")) return designedPage("waiting", simBar);
    const w = cfgOf("waiting");
    const serving = me.status === "serving";
    const called = me.status === "called";
    const thread = me.messages ?? [];
    return card(
      <>
        {simBar}
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: T.space[2], padding: `${T.space[2]} 0` }}>
          {serving ? (
            <>
              {/* Uppercase tracked overlines are `SectionLabel`. This one also
                  carried `letterSpacing: .06` — a BARE number, which React emits
                  as 0.06*px* (invisible), not the 0.06em the author meant; the
                  component tracks it from `T.letterSpacing.wide`. */}
              <SectionLabel tone="success">{t('queues.join.text11')}</SectionLabel>
              <div style={{ fontSize: T.text.display.fontSize, fontWeight: 800, color: T.n1 }}>{t('queues.join.text12')}{" "}{me.name.split(" ")[0]}</div>
            </>
          ) : called ? (
            // TOKEN ROLE: was `background: T.green1` + `color: T.white`. `green1`
            // is a FOREGROUND accent that inverts to #4ECB8E under (ui-raw: hex in prose)
            // [data-theme="dark"], so the solid "it's your turn" fill turned pale
            // green with dark ink there. `successFill` is fixed in both themes and
            // is the token meant to carry `onColor`.
            <div data-testid="qjoin-called-banner" style={{ background: T.successFill, borderRadius: T.radius.lg, padding: `${T.space[6]} ${T.space[5]}`, color: T.onColor, display: "flex", flexDirection: "column", gap: T.space[2], alignItems: "center", boxShadow: `0 0 0 6px ${alpha(T.successFill, 18)}` }}>
              {/* Not IconTile: this is the INVERSE of a solid tone tile (onColor
                  fill, tone glyph) and IconTile's solid variant always pairs a
                  tone fill with onColor ink. Noted in the report. */}
              <span style={{ width: 46, height: 46, borderRadius: T.radius.pill, background: T.onColor, color: T.successFill, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><Icon.check size={24}/></span>
              <SectionLabel style={{ color: T.onColor, fontWeight: 800, opacity: .92 }}>{t('queues.join.text13')}</SectionLabel>
              {/* 26px hero: OFF the type ramp (display tops out at 22). Left raw
                  deliberately — crushing it to 22 would flatten this page's one
                  moment of hierarchy. Reported, not tokenized. */}
              <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{t('queues.join.text14')}</div>
              <div style={{ fontSize: T.text.body.fontSize, opacity: .92, lineHeight: 1.45 }}>{t('queues.join.text15')}</div>
            </div>
          ) : (
            <>
              <SectionLabel>{w.headline}</SectionLabel>
              {/* 44px mono position numeral: OFF the type ramp (see above). Raw
                  and reported rather than crushed to 22. */}
              {w.showPosition !== false ? <div style={{ fontSize: 44, fontWeight: 800, color: T.n1, fontFamily: T.fontMono, lineHeight: 1 }}>#{me.position}</div> : null}
              {w.showEstWait !== false ? <div style={{ fontSize: T.text.body.fontSize, color: T.n2 }}>{t('queues.join.text16')}{" "}<b>{me.estWaitMins} {t('queues.join.text17')}</b> {t('queues.join.text18')}</div> : null}
            </>
          )}
        </div>
        {/* Key-value review rows are the DS `SummaryList` / `SummaryRow`, not
            three hand-rolled space-between flex rows. SummaryRow also fixed the
            long-label/long-value COLLISION these rows still had. */}
        <SummaryList testid="qjoin-entry-summary">
          <SummaryRow label={t('queues.join.text19')} value={q.name} />
          <SummaryRow label={t('queues.join.text20')} value={q.waiting} />
          {/* ui-config: per-channel third-party brand mark (see CH_META). */}
          <SummaryRow label={t('queues.join.text21')}>
            <span style={{ fontWeight: 700, color: CH_META[me.channel].color }}>{CH_META[me.channel].label}</span>
          </SummaryRow>
        </SummaryList>
        {w.allowMessaging !== false ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], borderTop: `1px solid ${T.border}`, paddingTop: T.space[4] }}>
            <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 700, color: T.n1 }}>{t('queues.join.text22')}</div>
            {thread.length ? (
              <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], maxHeight: 160, overflow: "auto" }}>
                {/* Directional message bubbles are the DS `ChatBubble` — it owns
                    the side alignment, the tailed corner and the brandFill/onColor
                    outgoing pair the hand-rolled div wrote as n1/white. */}
                {thread.slice(-6).map((m, i) => (
                  <ChatBubble key={i} side={m.from === "you" ? "out" : "in"} maxWidth="82%">{m.text}</ChatBubble>
                ))}
              </div>
            ) : (
              // Sibling surfaces get the SAME empty treatment: this thread and
              // the Location picker below both use `InlineEmptyState`. An empty
              // thread previously rendered NOTHING at all.
              <InlineEmptyState size="sm" title={t('queues.join.emptyThread', "No messages yet")}
                description={t('queues.join.emptyThreadSub', "Send the team a message and it appears here.")}
                testid="qjoin-thread-empty" />
            )}
            <div style={{ display: "flex", gap: T.space[2], flexWrap: "wrap" }}>
              {/* Suggested replies are the DS `Chip` (pill shell + focus ring). */}
              {((w.quickReplies ?? []) as string[]).slice(0, 3).map((r) => (
                <Chip key={r} onClick={() => setMsgText(r)} aria-label={t('queues.join.ariaLabel', { r })} testid="qjoin-quick-reply">{r}</Chip>
              ))}
            </div>
            <div style={{ display: "flex", gap: T.space[2] }}>
              {/* ui-raw: control box only — same 48/16px touch reason as the
                  `input` recipe at the top of this file (Input fixes height at 40
                  and field text at 14, neither exposed as a prop). */}
              <input value={msgText} onChange={(e) => setMsgText(e.target.value)} placeholder={t('queues.join.placeholder')} style={{ ...input, flex: 1, minHeight: 44 }} data-testid="qjoin-msg-input"
                onKeyDown={(e) => { if (e.key === "Enter") sendMsg(msgText); }} />
              {/* Sibling of the `cta` recipe: Btn's own `primary` fill, geometry
                  only overridden to match the 44px composer field beside it. */}
              <Btn onClick={() => sendMsg(msgText)} disabled={msgSending || !msgText.trim()} testid="qjoin-send-btn"
                style={{ height: 44, padding: `0 ${T.space[5]}`, borderRadius: T.radius.default, fontSize: T.text.body.fontSize, fontWeight: 700, flexShrink: 0, justifyContent: "center", opacity: msgSending || !msgText.trim() ? .5 : 1 }}>{t('queues.join.text23')}</Btn>
            </div>
          </div>
        ) : null}
        {w.showBrowse !== false ? (
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[2], borderTop: `1px solid ${T.border}`, paddingTop: T.space[4] }}>
            <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 700, color: T.n1 }}>{w.whileHeading ?? "While you wait"}</div>
            <a href={w.browseUrl || undefined} target="_blank" rel="noopener noreferrer" style={linkBtn} data-testid="qjoin-browse-link"><Icon.globe size={16}/>{w.browseLabel ?? "Browse our Services and Experiences"}</a>
          </div>
        ) : null}
        {!serving ? (
          // Btn's own secondary+danger treatment (red ink on the card surface,
          // its own border + focus ring) instead of a hand-written fill/ink/
          // border triple. Only the 48px touch height stays local.
          <Btn variant="secondary" danger onClick={leave} disabled={busy} testid="qjoin-leave-btn" full
            style={{ height: 48, fontWeight: 700, padding: 0, opacity: busy ? .6 : 1 }}>
            {t('queues.join.text24')}</Btn>
        ) : null}
        {err ? <Banner tone="danger" size="compact" dismissible={false} testid="qjoin-waiting-error">{err}</Banner> : null}
      </>,
      undefined, themeOf("waiting"), "waiting"
    );
  }

  if (q.status !== "open") {
    return card(
      <div style={{ textAlign: "center", padding: `${T.space[2]} 0`, display: "flex", flexDirection: "column", gap: T.space[2] }}>
        <div style={{ fontSize: T.text.h2.fontSize, fontWeight: 800, color: T.n1 }}>{q.status === "paused" ? t('queues.join.text25') : t('queues.join.text26')}</div>
        <div style={{ fontSize: T.text.body.fontSize, color: T.soft }}>{q.status === "paused" ? t('queues.join.text27') : t('queues.join.text28')}</div>
      </div>
    );
  }

  /* ── Pre-join walk: Services → Wait Time → Contact Details ─────────── */
  // Progress DOTS (an expanding-pill position indicator), not jrni-ui `Stepper`,
  // which is a labelled numbered step rail — a different component for a
  // different job. No DS export covers a dot indicator; proposal filed.
  const stepDots = walkPages.length > 1 ? (
    <div role="progressbar" aria-valuemin={1} aria-valuemax={walkPages.length} aria-valuenow={walk + 1}
      aria-label={t('queues.join.stepProgress', "Step progress")}
      style={{ display: "flex", gap: T.space[1], justifyContent: "center" }}>
      {walkPages.map((p, i) => <span key={p} style={{ width: i === walk ? 18 : 7, height: 7, borderRadius: T.radius.pill, background: i === walk ? T.n1 : T.n4, transition: "width .15s" }} />)}
    </div>
  ) : null;
  // ui-raw: the back affordance is a text link with NO control box — auto height,
  // zero padding, aligned to the card's text edge. Btn's `link` variant still
  // renders at a fixed control height (28/36/44px) with horizontal padding, which
  // would push this off that edge and add ~20px above every walk page.
  const backBtn = walk > 0 ? (
    <button /* ui-raw: text-link back affordance with no control box (reasoning above) */ onClick={() => setWalk((s) => s - 1)} data-testid="qjoin-back-btn" style={{ background: "none", border: "none", color: T.soft, fontSize: T.text.bodySm.fontSize, fontWeight: 600, fontFamily: T.font, cursor: "pointer", alignSelf: "flex-start", padding: 0, display: "inline-flex", alignItems: "center", gap: T.space[1] }}><Icon.arrow dir="left" size={12}/> {t('queues.join.text29')}</button>
  ) : null;
  const walkTop = (backBtn || stepDots) ? (
    <div style={{ display: "flex", alignItems: "center", minHeight: 20 }}>{backBtn}<span style={{ flex: 1 }}/>{stepDots}</div>
  ) : null;

  if (curPage === "prebook") {
    if (designOf("prebook")) return designedPage("prebook", walkTop);
    const c = cfgOf("prebook");
    const sects = qResolveQuestionSections((c.bookingQuestions ?? []) as QQuestionRef[], questionLib);
    return card(
      <>
        {backBtn}
        <div><div style={{ fontSize: T.text.h2.fontSize, fontWeight: 800, color: T.n1 }}>{c.headline}</div><div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: T.space[1] }}>{c.sub}</div></div>
        {sects.length === 0 ? (
          <InlineEmptyState size="sm" title={t('queues.join.emptyPrebook', "Nothing to answer")}
            description={t('queues.join.emptyPrebookSub', "Continue to join the queue.")} testid="qjoin-prebook-empty" />
        ) : null}
        {sects.map((sect, si) => (
          <div key={sect.title ?? `s${si}`} style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
            {sect.title ? <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 800, color: T.n1 }}>{sect.title}</div> : null}
            {sect.questions.map((qq) => <QQuestionField key={qq.id} q={qq} value={answers[qq.id] ?? ""} onChange={(v) => setAnswers((a) => ({ ...a, [qq.id]: v }))} />)}
          </div>
        ))}
        <Btn onClick={() => setWalk((x) => x + 1)} testid="qjoin-prebook-continue" full style={{ ...cta, height: 52, padding: 0 }}>{c.continueLabel ?? "Continue to the queue"}</Btn>
        {stepDots}
      </>,
      undefined, themeOf("prebook"), "prebook"
    );
  }

  if (curPage === "services") {
    if (designOf("services")) return designedPage("services", walkTop);
    const c = cfgOf("services");
    const waits = q.serviceWaits ?? {};
    return card(
      <>
        {backBtn}
        <div><div style={{ fontSize: T.text.h2.fontSize, fontWeight: 800, color: T.n1 }}>{c.headline}</div><div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: T.space[1] }}>{c.sub}</div></div>
        <div style={{ display: "flex", flexDirection: "column", gap: T.space[3] }} role="radiogroup" aria-label={c.headline}>
          {(q.serviceGroups && q.serviceGroups.length ? q.serviceGroups : [{ label: null, services: q.services }]).map((g, gi) => (
            <div key={g.label ?? `g${gi}`} style={{ display: "flex", flexDirection: "column", gap: T.space[2] }}>
              {g.label ? <SectionLabel>{g.label}</SectionLabel> : null}
              <div style={c.layout === "grid" ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[2] } : { display: "flex", flexDirection: "column", gap: T.space[2] }}>
                {/* The previous `ui-raw` marker here claimed no jrni-ui component
                    matched "a composite list-item, not a single Btn action" — but
                    `SelectableTile` (title + muted description + radio indicator,
                    role="radio", constant-width border so selection never nudges
                    the row) is exactly that, and it shipped after the marker was
                    written. Adopted; the claim was stale, not wrong-at-the-time. */}
                {g.services.map((s) => (
                  <SelectableTile key={s} testid="qjoin-service-option"
                    selected={service === s}
                    onSelect={() => { setService(s); setWalk((x) => x + 1); }}
                    title={s}
                    description={c.showEstWait !== false ? `~${waits[s] ?? q.estWaitMins} ${t('queues.join.text30')}` : undefined}
                    style={{ minHeight: 52 }} />
                ))}
              </div>
            </div>
          ))}
          {q.services.length === 0 ? (
            <InlineEmptyState size="sm" title={t('queues.join.emptyServices', "No services to pick from")}
              description={t('queues.join.emptyServicesSub', "Ask a member of staff to help you join.")} testid="qjoin-services-empty" />
          ) : null}
        </div>
        {stepDots}
      </>,
      undefined, themeOf("services"), "services"
    );
  }

  if (curPage === "locations") {
    if (designOf("locations")) return designedPage("locations", walkTop);
    const c = cfgOf("locations");
    const opts = [...(q.nearbyLocations ?? []).map((l) => ({ id: l.id, name: l.name, distanceKm: l.distanceKm, waitMins: (l.serviceWaits ?? {})[service || q.services[0] || ""] ?? l.estWaitMins ?? q.estWaitMins }))]
      .sort((a, b) => locSort === "wait" ? a.waitMins - b.waitMins || a.distanceKm - b.distanceKm : a.distanceKm - b.distanceKm)
      .slice(0, Math.max(1, Math.min(10, Number(c.maxLocations ?? 10))));
    return card(
      <>
        {backBtn}
        <div><div style={{ fontSize: T.text.h2.fontSize, fontWeight: 800, color: T.n1 }}>{c.headline}</div><div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: T.space[1] }}>{c.sub} {t('queues.join.text31')}{" "}<b style={{ color: T.n1 }}>{service || q.services[0]}</b></div></div>
        {c.allowSortByWait !== false ? (
          // The previous `ui-raw` marker rejected SegmentedControl because it
          // renders one inset track rather than separate bordered pills — true,
          // but `ChipToggleGroup` IS the separate-bordered-pill toggle group (and
          // adds roving tabindex + arrow-key navigation + a real radiogroup role,
          // where this row was five same-tab-stop `aria-pressed` buttons).
          <ChipToggleGroup ariaLabel={t('queues.join.ariaLabel2')} testid="qjoin-locsort"
            options={[{ value: "distance", label: "Nearest first" }, { value: "wait", label: "Shortest wait" }]}
            value={locSort}
            onChange={(v) => setLocSort(v as "distance" | "wait")}
            style={{ gap: T.space[2] }} />
        ) : null}
        <div style={{ display: "flex", flexDirection: "column", gap: T.space[2] }} role="radiogroup" aria-label={c.headline}>
          {/* Same `SelectableTile` as the service picker — its `trailing` slot
              (added 2026-08-10) carries the wait figure that used to force a
              hand-rolled two-column button here. */}
          {opts.map((l) => (
            <SelectableTile key={l.id} testid="qjoin-location-option" data-id={l.id}
              selected={locId === l.id}
              onSelect={() => { setLocId(l.id); setWalk((x) => x + 1); }}
              title={l.name}
              description={`${l.distanceKm} ${t('queues.join.text32')}`}
              trailing={
                <span style={{ textAlign: "right", display: "inline-block" }}>
                  <span style={{ display: "block", fontSize: T.text.h2.fontSize, fontWeight: 800, color: T.n1, fontFamily: T.fontMono }}>~{l.waitMins}<span style={{ fontSize: T.text.label.fontSize, fontWeight: 700 }}> {t('queues.join.text17')}</span></span>
                  <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{t('queues.join.text33')}</span>
                </span>
              }
              style={{ minHeight: 56, alignItems: "center" }} />
          ))}
          {/* Same empty treatment as the message thread and the service picker. */}
          {opts.length === 0 ? (
            <InlineEmptyState size="sm" title={t('queues.join.text34')} testid="qjoin-locations-empty" />
          ) : null}
        </div>
        {opts.length === 0 ? <Btn onClick={() => setWalk((x) => x + 1)} full style={{ ...cta, height: 52, padding: 0 }}>{t('queues.join.text35')}</Btn> : null}
        {stepDots}
      </>,
      undefined, themeOf("locations"), "locations"
    );
  }

  if (curPage === "wait") {
    if (designOf("wait")) return designedPage("wait", walkTop);
    const c = cfgOf("wait");
    const est = (q.serviceWaits ?? {})[service] ?? q.estWaitMins;
    return card(
      <>
        {backBtn}
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: T.space[2], padding: `${T.space[1]} 0` }}>
          <SectionLabel tone="warning">{service || q.services[0]}</SectionLabel>
          <div style={{ fontSize: T.text.h2.fontSize, fontWeight: 800, color: T.n1 }}>{c.headline}</div>
          {/* 46px mono hero: OFF the type ramp (display tops out at 22). Left raw
              on purpose and reported — this figure IS the page. */}
          <div style={{ fontSize: 46, fontWeight: 800, color: T.n1, fontFamily: T.fontMono, lineHeight: 1 }}>~{est}<span style={{ fontSize: T.text.h2.fontSize, fontWeight: 700 }}> {t('queues.join.text17')}</span></div>
          <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft }}>{q.waiting} {t('queues.join.text36')}</div>
        </div>
        <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, textAlign: "center" }}>{c.sub}</div>
        <Btn onClick={() => setWalk((x) => x + 1)} testid="qjoin-wait-continue" full style={{ ...cta, height: 52, padding: 0 }}>{c.continueLabel}</Btn>
        {c.showBook !== false ? <a href={c.bookUrl || undefined} target="_blank" rel="noopener noreferrer" style={linkBtn} data-testid="qjoin-book-link"><Icon.calendar size={16}/>{c.bookLabel ?? "I can't wait — book an appointment"}</a> : null}
        {stepDots}
      </>,
      undefined, themeOf("wait"), "wait"
    );
  }

  // Contact Details
  if (designOf("contact")) return designedPage("contact", walkTop);
  const c = cfgOf("contact");
  const f = (c.fields ?? { firstName: "required", lastName: "required", mobile: "required", email: "optional" }) as Record<string, Mode>;
  const rq = (c.resolvedQuestions ?? []) as { id: string; label: string; type?: string; options?: string[] }[];
  return card(
    <>
      {backBtn}
      <div>
        <div style={{ fontSize: T.text.h2.fontSize, fontWeight: 800, color: T.n1 }}>{c.headline}</div>
        <div style={{ fontSize: T.text.bodySm.fontSize, color: T.soft, marginTop: T.space[1] }}>{c.sub} · {q.waiting} {t('queues.join.text37')}{(q.serviceWaits ?? {})[service] ?? q.estWaitMins} {t('queues.join.text17')}</div>
      </div>
      {/* Every label + required marker below is the DS `Field` — it replaced this
          screen's local `label` style object AND its `req()` asterisk helper, two
          more copies of what Field already owns. Only the CONTROL box stays raw.
          ui-raw (both fields): 48px touch height + 16px field text, neither of
          which jrni-ui `Input` exposes — full reasoning on the `input` recipe. */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[3] }}>
        {f.firstName !== "off" ? <Field label={t('queues.join.text38')} required={f.firstName === "required"}><input /* ui-raw: 48px touch height + 16px field text (reasoning above) */ value={first} onChange={(e) => setFirst(e.target.value)} style={input} autoComplete="given-name" data-testid="qjoin-first-input" /></Field> : null}
        {f.lastName !== "off" ? <Field label={t('queues.join.text39')} required={f.lastName === "required"}><input /* ui-raw: 48px touch height + 16px field text (reasoning above) */ value={last} onChange={(e) => setLast(e.target.value)} style={input} autoComplete="family-name" data-testid="qjoin-last-input" /></Field> : null}
      </div>
      {/* ui-raw (both fields): same control-box reason as the pair above. */}
      {f.mobile !== "off" ? <Field label={t('queues.join.text40')} required={f.mobile === "required"}><input value={phone} onChange={(e) => setPhone(e.target.value)} style={input} inputMode="tel" autoComplete="tel" placeholder="+1 —" data-testid="qjoin-phone-input" /></Field> : null}
      {f.email !== "off" ? <Field label={t('queues.join.text41')} required={f.email === "required"}><input /* ui-raw: 48px touch height + 16px field text (reasoning above) */ value={email} onChange={(e) => setEmail(e.target.value)} style={input} inputMode="email" autoComplete="email" data-testid="qjoin-email-input" /></Field> : null}
      {c.askChannel !== false ? (
        <Field label={t('queues.join.text42')}>
          {/* The SELECTED state is painted in the channel's own third-party brand
              colour (CH_META — vendor DATA, not a token). `ChipToggleGroupOption`
              is `{ value, label, count, disabled }` and `SegmentedControl` paints
              every active segment `T.p3`/`T.p1` — neither exposes a per-option
              colour hook (verified against their source during this migration),
              so this stays ui-raw. It is now a real `role="radiogroup"` of
              `role="radio"` buttons; it used to be two `aria-pressed` buttons. */}
          <div style={{ display: "flex", gap: T.space[2] }} role="radiogroup" aria-label={t('queues.join.text42')}>
            {(["sms", "whatsapp"] as MsgChannel[]).map((ch) => (
              <button /* ui-raw: per-option third-party brand colour, real radiogroup (reasoning above) */ key={ch} type="button" role="radio" onClick={() => setChannel(ch)} aria-checked={channel === ch} data-testid={`qjoin-channel-${ch}`}
                style={{ flex: 1, minHeight: 48, borderRadius: T.radius.default, border: `1.5px solid ${channel === ch ? CH_META[ch].color : T.border}`, background: channel === ch ? alpha(CH_META[ch].color, 10) : T.white, color: channel === ch ? CH_META[ch].color : T.n1, fontSize: T.text.body.fontSize, fontWeight: 700, fontFamily: T.font, cursor: "pointer" }}>
                {CH_META[ch].label}
              </button>
            ))}
          </div>
        </Field>
      ) : null}
      {walkPages.length === 1 && q.services.length > 1 ? (
        <Field label={t('queues.join.text43')}>
          {/* ui-raw: jrni-ui Dropdown replaces the native OS picker with a
              custom JS menu — a real interaction-model change on this
              customer-facing mobile surface (loses native mobile select UX). */}
          <select value={service || q.services[0]} onChange={(e) => setService(e.target.value)} style={{ ...input, background: T.white }} data-testid="qjoin-service-select">
            {q.services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      ) : null}
      {c.askParty !== false ? (
        <Field label={t('queues.join.text44')}><NumberField value={party} min={1} onChange={setParty} ariaLabel={t('queues.join.ariaLabel3')} style={input} /></Field>
      ) : null}
      {rq.map((qq) => <QQuestionField key={qq.id} q={qq as QQField} value={answers[qq.id] ?? ""} onChange={(v) => setAnswers((a) => ({ ...a, [qq.id]: v }))} />)}
      {svcSectionsFor(service || q.services[0] || "").map((sect, si) => (
        <div key={sect.title ?? `svc${si}`} style={{ display: "flex", flexDirection: "column", gap: T.space[3] }}>
          {sect.title ? <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 800, color: T.n1, borderTop: `1px solid ${T.border}`, paddingTop: T.space[3] }}>{sect.title}</div> : null}
          {sect.questions.filter((qq) => !rq.some((y) => y.id === qq.id)).map((qq) => <QQuestionField key={qq.id} q={qq} value={answers[qq.id] ?? ""} onChange={(v) => setAnswers((a) => ({ ...a, [qq.id]: v }))} />)}
        </div>
      ))}
      {/* Same Banner treatment as every other error on this screen. */}
      {err ? <Banner tone="danger" size="compact" dismissible={false} testid="qjoin-contact-error">{err}</Banner> : null}
      <Btn onClick={() => join()} disabled={busy} testid="qjoin-join-btn" full style={{ ...cta, height: 52, padding: 0, opacity: busy ? .6 : 1 }}>
        {busy ? t('queues.join.text45') : c.joinLabel ?? "Join the queue"}
      </Btn>
      {stepDots}
    </>,
    undefined, themeOf("contact"), "contact"
  );
}
